import { useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { CitationPanel } from "@/components/citations";
import { useCanEdit } from "@/lib/project-access";
import { useWalkthroughStream } from "@/hooks/use-walkthrough-stream";

// Feature subcomponents
import { LiveWalkthroughTopbar } from "@/components/live-walkthrough/live-walkthrough-topbar";
import { LiveWalkthroughScreenshot } from "@/components/live-walkthrough/live-walkthrough-screenshot";
import { LiveWalkthroughTabs } from "@/components/live-walkthrough/live-walkthrough-tabs";
import { LiveWalkthroughFooter } from "@/components/live-walkthrough/live-walkthrough-footer";
import { useLiveWalkthroughState } from "@/components/live-walkthrough/use-live-walkthrough-state";
import type { LiveWalkthroughData } from "@/components/live-walkthrough/types";

export function LiveWalkthroughViewer({
  walkthrough,
  personaName,
  scenarioName,
  projectId,
}: {
  walkthrough: LiveWalkthroughData;
  personaName: string;
  scenarioName: string;
  projectId: string;
}) {
  const stream = useWalkthroughStream();
  const isRunning =
    walkthrough.status === "running" || walkthrough.status === "pending";
  const isCancelled =
    walkthrough.status === "cancelled" || stream.error === "Cancelled by user";
  const isComplete =
    walkthrough.status === "completed" || stream.status === "done";
  const isErrored =
    !isCancelled &&
    (walkthrough.status === "failed" || stream.status === "error");
  const isLocalLive = Boolean(walkthrough.liveState);
  const phase = stream.phase ?? walkthrough.liveState?.phase ?? null;

  useEffect(() => {
    if (isRunning && stream.status === "idle") {
      stream.connect(walkthrough.id);
    }
  }, [isRunning, stream.status, walkthrough.id, stream.connect]);

  const utils = trpc.useUtils();
  const canEdit = useCanEdit();
  const cancelMutation = trpc.walkthrough.cancel.useMutation({
    onSuccess: () => {
      utils.walkthrough.getById.invalidate({ id: walkthrough.id });
    },
  });

  useEffect(() => {
    if (stream.steps.length === 0) return;
    utils.walkthrough.getById.invalidate({ id: walkthrough.id });
  }, [stream.steps.length, utils.walkthrough.getById, walkthrough.id]);

  const state = useLiveWalkthroughState({
    walkthrough,
    streamSteps: stream.steps,
    streamLiveFindings: stream.liveFindings,
    isRunning,
    isComplete,
    isErrored,
  });

  let hostname = "Live URL";
  try {
    if (walkthrough.targetUrl)
      hostname = new URL(walkthrough.targetUrl).hostname;
  } catch {
    // invalid URL
  }

  const hasHtmlReport = walkthrough.artifacts.some(
    (a) => a.type === "html_report" && a.content,
  );
  const backTo = walkthrough.scenarioId
    ? `/projects/${projectId}/scenarios/${walkthrough.scenarioId}`
    : `/projects/${projectId}`;
  const backLabel = walkthrough.scenarioId
    ? scenarioName || "Scenario"
    : "Project";
  const topbarTitle = [personaName, scenarioName]
    .filter(Boolean)
    .join(" \u00D7 ");

  return (
    <>
      <LiveWalkthroughTopbar
        title={topbarTitle}
        backTo={backTo}
        backLabel={backLabel}
      />

      <div
        ref={state.containerRef}
        className="flex flex-1 flex-col overflow-hidden"
      >
        {/* Mobile View Switcher */}
        <div className="flex border-b border-border/60 bg-muted/30 p-1 lg:hidden">
          {(
            [
              ["screenshot", "Screenshot"],
              ["timeline", `Timeline (${state.displaySteps.length})`],
              ["findings", `Findings (${state.unifiedFindings.length})`],
              ["designer", "Designer"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => state.setMobileTab(key)}
              className={cn(
                "flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors",
                state.mobileTab === key
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Screenshot Pane with Live Time Scrubber */}
          <LiveWalkthroughScreenshot
            hostname={hostname}
            isLocalLive={isLocalLive}
            runnerName={stream.runner?.name}
            displayedStep={state.displayedStep}
            displayStepsCount={state.displaySteps.length}
            steps={state.displaySteps}
            canEdit={canEdit}
            isRunning={isRunning}
            isComplete={isComplete}
            isErrored={isErrored}
            isStopping={cancelMutation.isPending}
            onStop={() => cancelMutation.mutate({ id: walkthrough.id })}
            screenshotLoading={state.screenshotLoading}
            screenshotBlobUrl={state.screenshotBlobUrl}
            activeStepIdx={state.activeStepIdx}
            onStepChange={state.handleStepChange}
            isLive={state.isLive}
            stepsBehindLive={state.stepsBehindLive}
            onResumeLive={state.handleResumeLive}
            findingsByStep={state.findingsByStep}
            visibleOnMobile={state.mobileTab === "screenshot"}
            width={state.dividerX}
            streamStatus={stream.status}
            isStalled={stream.isStalled}
            stepStartedAt={stream.stepStartedAt}
            activeActionStartedAt={stream.activeAction?.startedAt}
          />

          {/* Desktop Resizer Handle */}
          <button
            type="button"
            aria-label="Resize screenshot pane"
            onMouseDown={state.handleDividerMouseDown}
            className="group hidden lg:flex w-1 shrink-0 cursor-col-resize bg-border/60 transition-colors hover:bg-primary/60 focus-visible:bg-primary/60 focus-visible:outline-none"
          />

          {/* Right Tabbed Panel */}
          <LiveWalkthroughTabs
            state={state}
            isRunning={isRunning}
            isComplete={isComplete}
            isErrored={isErrored}
            isCancelled={isCancelled}
            isLocalLive={isLocalLive}
            phase={phase}
            streamError={stream.error}
            errorMessage={walkthrough.errorMessage}
            activeAction={stream.activeAction}
            completion={stream.completion}
            projectId={projectId}
            walkthroughId={walkthrough.id}
          />
        </div>

        {/* Global Footer Summary */}
        <LiveWalkthroughFooter
          totalFindings={state.unifiedFindings.length}
          severityCounts={state.severityCounts}
          helpsCount={state.helpsCount}
          onSelectFindingFilter={state.handleSelectFindingFilter}
          hasHtmlReport={hasHtmlReport}
          onExportReport={state.handleExport}
          isComplete={isComplete}
          projectId={projectId}
          walkthroughId={walkthrough.id}
        />
      </div>

      <CitationPanel
        open={Boolean(state.panelCitation)}
        onOpenChange={(open) => !open && state.setPanelCitation(null)}
        sourceType={state.panelCitation?.sourceType ?? null}
        sourceId={state.panelCitation?.sourceId ?? null}
        projectId={projectId}
      />
    </>
  );
}
